import { View, DataView, ContextMenu, Dialog } from 'web-mojo';
import { Route } from '@portal/models/Route.js';

const STATUS_ICON = {
    pending:     '<i class="bi bi-circle text-muted"></i>',
    arrived:     '<i class="bi bi-circle-fill text-primary"></i>',
    in_progress: '<i class="bi bi-circle-fill text-warning"></i>',
    completed:   '<i class="bi bi-check-circle-fill text-success"></i>',
    skipped:     '<i class="bi bi-skip-forward-fill text-secondary"></i>',
};

const STATUS_BADGES = {
    scheduled:   '<span class="badge bg-secondary">Scheduled</span>',
    in_progress: '<span class="badge bg-primary">In Progress</span>',
    completed:   '<span class="badge bg-success">Completed</span>',
    cancelled:   '<span class="badge bg-dark">Cancelled</span>',
    draft:       '<span class="badge bg-light text-dark border">Draft</span>',
};


class RouteView extends View {
    constructor(options = {}) {
        super({
            className: 'route-view',
            ...options
        });

        this.template = `
            <div data-container="header"></div>
            <div data-container="details-view"></div>
            <div data-container="progress-view"></div>
            <div data-container="stops-view"></div>
        `;
    }

    async onInit() {
        const id = this.model.get('id');
        if (id) {
            const [routeResp, stopsResp] = await Promise.all([
                app.api.get(`/api/routes/route/${id}`, { graph: 'detail' }),
                app.api.get('/api/routes/stop', { route: id, sort: 'sequence_position', size: 200 }),
            ]);
            if (routeResp.success && routeResp.data.status) {
                const data = routeResp.data.data;
                data.stops_list = (stopsResp.success && stopsResp.data.status)
                    ? stopsResp.data.data || []
                    : [];
                this.model.set(data);
            }
        }

        const status = this.model.get('status') || 'scheduled';

        // Header
        this.header = new View({
            containerId: 'header',
            template: `
            <div class="d-flex justify-content-between align-items-start mb-3">
                <div class="d-flex align-items-center gap-3">
                    <div class="d-flex align-items-center justify-content-center rounded bg-light"
                         style="width:42px;height:42px;flex-shrink:0">
                        <i class="bi bi-signpost-split fs-5 text-secondary"></i>
                    </div>
                    <div>
                        <div class="d-flex align-items-center gap-2 mb-1">
                            <h5 class="mb-0">{{model.template.name|default('Route')}}</h5>
                            {{{statusBadge}}}
                        </div>
                        <div class="text-muted small">{{model.scheduled_date|default('—')}}</div>
                    </div>
                </div>
                <div data-container="context-menu"></div>
            </div>`
        });
        this.header.statusBadge = STATUS_BADGES[status] || `<span class="badge bg-secondary">${status}</span>`;
        this.header.setModel(this.model);
        this.addChild(this.header);

        // Context menu
        const menuItems = [];
        if (status === 'scheduled') {
            menuItems.push({ label: 'Cancel Route', action: 'cancel-route', icon: 'bi-x-circle', className: 'text-danger' });
        }
        if (menuItems.length > 0) {
            const contextMenu = new ContextMenu({
                containerId: 'context-menu',
                className: 'context-menu-view header-menu-absolute',
                context: this.model,
                config: { icon: 'bi-three-dots-vertical', items: menuItems }
            });
            this.addChild(contextMenu);
        }

        // Details
        this.detailsView = new DataView({
            model: this.model,
            containerId: 'details-view',
            className: 'mb-3',
            showEmptyValues: true,
            fields: [
                { name: 'template.name', label: 'Route Template', colSize: 6 },
                { name: 'scheduled_date', label: 'Date', colSize: 6 },
                { name: 'operator.display_name', label: 'Operator', colSize: 6 },
            ]
        });
        this.addChild(this.detailsView);

        // Progress bar
        const progress = this.model.get('progress');
        this.progressView = new View({ containerId: 'progress-view' });
        if (progress && progress.total > 0) {
            const pct = Math.round(progress.completed / progress.total * 100);
            this.progressView.progressPct = pct;
            this.progressView.progressLabel = `${progress.completed} / ${progress.total} stops`;
            this.progressView.template = `
            <div class="px-3 mb-3">
                <div class="d-flex justify-content-between small text-muted mb-1">
                    <span>{{progressLabel}}</span>
                    <span>{{progressPct}}%</span>
                </div>
                <div class="progress" style="height:6px;">
                    <div class="progress-bar" style="width:{{progressPct}}%"></div>
                </div>
            </div>`;
        } else {
            this.progressView.template = '';
        }
        this.addChild(this.progressView);

        // Stops timeline
        const stops = this.model.get('stops_list') || [];
        this.stopsView = new View({ containerId: 'stops-view' });
        this.stopsView.stops = stops.map((stop, i) => ({
            statusIcon: STATUS_ICON[stop.status] || STATUS_ICON.pending,
            index: i + 1,
            displayName: stop.label || stop.location?.name || `Stop ${i + 1}`,
            activityType: stop.activity_type || '',
            completedTime: stop.completed_at
                ? new Date(stop.completed_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                : '',
        }));
        this.stopsView.hasStops = stops.length > 0;
        this.stopsView.template = `
        <div class="mb-3">
            <h6 class="text-muted small fw-semibold text-uppercase px-3 mb-2">Stops</h6>
            {{#hasStops}}
            <div class="border rounded mx-3">
                {{#stops}}
                <div class="d-flex align-items-start gap-2 py-2 px-3 border-bottom">
                    <div class="pt-1">{{{.statusIcon}}}</div>
                    <div class="small text-muted" style="min-width:1.4rem;">{{.index}}</div>
                    <div class="flex-grow-1">
                        <div class="small fw-semibold">{{.displayName}}</div>
                        {{#.activityType}}<div class="text-muted small">{{.activityType}}</div>{{/.activityType}}
                    </div>
                    {{#.completedTime}}<div class="text-muted small text-nowrap">{{.completedTime}}</div>{{/.completedTime}}
                </div>
                {{/stops}}
            </div>
            {{/hasStops}}
            {{^hasStops}}
            <p class="text-muted small px-3">No stops on this route.</p>
            {{/hasStops}}
        </div>`;
        this.addChild(this.stopsView);
    }

    async onActionCancelRoute() {
        const confirmed = await Dialog.confirm({
            title: 'Cancel Route',
            message: `Cancel "${this.model.get('template')?.name || 'this route'}" on ${this.model.get('scheduled_date')}?`,
            confirmText: 'Cancel Route',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await this.model.save({ cancel: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to cancel route');
            return;
        }
        app.toast.success('Route cancelled');
        this.render();
    }

    _onModelChange() {}
}

Route.VIEW_CLASS = RouteView;

export default RouteView;
